import { useEffect, useState, type ReactNode } from 'react'

import { get } from '@/api/client'
import { FullPageLoader } from '@/components/Loader'

/**
 * A host that belongs to no carrier goes to the marketing site.
 *
 * `acme.insuronix.ai` where no organization claims `acme` used to render the
 * whole sign-in page and refuse every credential silently. That is what
 * tenancy rule 2 asks for - a refusal says nothing - and it is also a form
 * that cannot work, shown to somebody who mistyped a name or followed a link
 * to a carrier that was never set up.
 *
 * So this asks the API once, before anything renders, and leaves. The cost is
 * stated plainly in `GET /auth/tenant` and in CLAUDE.md: the answer is now
 * probeable, so the carrier list is enumerable by anyone who cares to try
 * names. That is a deliberate trade, not an oversight.
 *
 * It fails **open**. If the check cannot be made - the API is down, the
 * network blinked - the sign-in page renders. A carrier locked out of their
 * own product because a health check failed would be a far worse outcome than
 * a stranger seeing a login form.
 */

interface TenantAnswer {
  known: boolean
  marketingSiteUrl: string
}

export function TenantGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'here' | 'leaving'>('checking')

  useEffect(() => {
    let cancelled = false
    get<TenantAnswer>('/auth/tenant')
      .then((answer) => {
        if (cancelled) return
        if (answer.known) {
          setState('here')
          return
        }
        setState('leaving')
        // replace, not assign: the dead host should not be a back-button away.
        window.location.replace(answer.marketingSiteUrl)
      })
      .catch(() => {
        if (!cancelled) setState('here')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (state === 'here') return <>{children}</>
  return <FullPageLoader label={state === 'leaving' ? 'Taking you to insuronix.ai' : 'Loading Insuronix'} />
}

export default TenantGate
